import { closeModal, render, useUeResumePrefill } from '~/legacy/runtime'
import { ENTITY_DEFS } from '~/data/entities'
import { fieldsOf } from '~/utils/entityColumns'
import type { Field } from '~/utils/entityColumns'
import { apiCreate, apiUpdate } from '~/composables/useApi'
import { ECOLE_SCOPES } from '~/composables/useDb'
import type { Row } from '~/composables/useDb'

/**
 * `submitForm` de la maquette : enregistre le formulaire ouvert par `openForm` (le bouton « + Nouveau » d'une liste, le crayon
 * « Modifier » d'une fiche ou d'une ligne, « Modifier cette école »). Le formulaire lui-même (`openForm`, dans `legacy/screens.ts`)
 * est repris tel quel de la maquette ; seul l'enregistrement change : au lieu d'écrire dans une base en mémoire, chaque champ
 * part vers l'API Java, entité par entité, exactement selon les mêmes règles (champs requis, valeurs fixes, numérotation,
 * cas particuliers des classes, des inscriptions, des offres et des unités d'enseignement).
 */

/** `fixedValues` de la maquette : les 4 vues de « personnes », et l'école courante pour les entités qui y sont limitées. */
const ROLE_FIXED: Record<string, string> = { etudiants: 'ETUDIANT', enseignants: 'ENSEIGNANT', parents: 'PARENT', personnelAdmin: 'PERSONNEL_ADMIN' }

function fixedValues(key: string): Record<string, unknown> {
  if (ROLE_FIXED[key]) return { role: ROLE_FIXED[key] }
  if (ECOLE_SCOPES[key]) return { ecoleId: useCurrentEcoleId().value }
  return {}
}

/** `typesDocumentForClasse` de la maquette : les types de document applicables à la classe (offre + niveau), ou tous si aucune classe. */
export function typesDocumentForClasse(classeId: string | null): Row[] {
  const db = useDb().value
  const classe = classeId ? findById('classes', classeId) : null
  return classe
    ? (db.typesDocument ?? []).filter(t => (t.offreIds as string[] ?? []).includes(classe.offreId as string) && (t.niveauIds as string[] ?? []).includes(classe.niveauId as string))
    : (db.typesDocument ?? [])
}

/** `missingRequiredDocuments` de la maquette. */
export function missingRequiredDocuments(inscriptionId: string | null): string[] {
  if (!inscriptionId) return []
  const db = useDb().value
  const inscription = findById('inscriptions', inscriptionId)
  const types = typesDocumentForClasse(inscription && inscription.classeId ? inscription.classeId as string : null)
  const required = types.filter(t => t.obligatoire)
  return required.filter((t) => {
    const doc = (db.documentsDossier ?? []).find(d => d.inscriptionId === inscriptionId && d.typeDocumentId === t.id)
    return !doc || doc.statut !== 'VALIDE'
  }).map(t => String(t.libelle))
}

/** Lecture d'un champ du formulaire (`#modal-fields`), comme la boucle de `submitForm`. */
function readField(container: HTMLElement, f: Field): { value: unknown, missing: boolean } {
  if (f.type === 'multiselect') {
    const checks = container.querySelectorAll<HTMLInputElement>(`.ms-check[data-field="${f.key}"]:checked`)
    const arr = [...checks].map(c => c.value)
    return { value: arr, missing: !!f.required && arr.length === 0 }
  }
  if (f.cascadeClasse) {
    const wrap = container.querySelector(`[data-cascade="${f.key}"]`)
    const classeVal = wrap?.querySelector<HTMLInputElement>('.ssel-classe .ssel-value')?.value
    const etuVal = wrap?.querySelector<HTMLInputElement>('.ssel-etudiant .ssel-value')?.value
    if (!classeVal || !etuVal) return { value: null, missing: true }
    const insc = (useDb().value.inscriptions ?? []).find(i => String(i.classeId) === classeVal && String(i.etudiantId) === etuVal)
    return { value: insc ? insc.id : null, missing: !insc }
  }
  if (f.type === 'fk' && f.ref === 'personnes') {
    const v = container.querySelector<HTMLInputElement>(`.ssel[data-field="${f.key}"] .ssel-value`)?.value ?? ''
    return { value: v === '' ? null : v, missing: !!f.required && !v }
  }
  const el = container.querySelector<HTMLInputElement>(`[data-field="${f.key}"]`)
  let value: unknown
  if (f.type === 'boolean') value = el?.checked ?? false
  else if (f.type === 'number') value = !el?.value ? null : Number(el.value)
  else if (f.type === 'fk') value = !el?.value ? null : el.value
  else value = el?.value ?? ''
  const missing = !!f.required && (value === '' || value === null || value === undefined)
  return { value, missing }
}

/** Bouton « + Nouvelle matière » du formulaire « Nouvelle UE » : garde les champs déjà saisis, ouvre la création de matière, et reprend la création de l'UE une fois la matière enregistrée (avec cette nouvelle matière déjà cochée). */
export function createMatiereFromUeForm(): void {
  const container = document.getElementById('modal-fields')
  if (!container) return
  const prefill: Record<string, unknown> = {}
  for (const f of fieldsOf('unitesEnseignement')) {
    prefill[f.key] = readField(container, f).value
  }
  closeModal()
  useUeResumePrefill().value = prefill
  openForm('matieres')
}

/** `submitForm` de la maquette. */
export async function submitForm(key: string, id: string | null): Promise<void> {
  const entity = ENTITY_DEFS[key]
  if (!entity) return
  const storeKey = entity.storageKey || key
  const container = document.getElementById('modal-fields')
  if (!container) return
  const ecueInlineIds = (key === 'unitesEnseignement' && !id)
    ? [...container.querySelectorAll<HTMLInputElement>('.ecue-inline-check:checked')].map(c => c.value)
    : []

  const data: Record<string, unknown> = {}
  const missing: string[] = []
  for (const f of fieldsOf(key)) {
    const { value, missing: isMissing } = readField(container, f)
    if (isMissing) missing.push(f.cascadeClasse ? 'Classe et étudiant' : f.label)
    data[f.key] = value
  }
  if (missing.length) {
    showToast(`Champs requis manquants : ${missing.join(', ')}`)
    return
  }

  let niveauIdsForOffre: string[] | null = null
  if (key === 'offres') {
    niveauIdsForOffre = (data.niveauIds as string[]) ?? []
    delete data.niveauIds
  }

  if (key === 'classes') {
    const valid = (useDb().value.niveauOffre ?? []).some(no => no.offreId === data.offreId && no.niveauId === data.niveauId)
    if (!valid) {
      const offreLabel = findById('offres', data.offreId as string)?.libelle
      showToast(`RG-03 : ce niveau ne fait pas partie des niveaux définis pour la formation « ${offreLabel} ». Vérifie « Niveaux par formation » dans Paramètres.`)
      return
    }
  }
  if (key === 'inscriptions' && data.statut === 'VALIDEE') {
    const docs = missingRequiredDocuments(id)
    if (docs.length) {
      showToast(`Validation impossible, documents obligatoires manquants : ${docs.join(', ')}`)
      return
    }
  }

  try {
    let finalId = id
    if (id) {
      const row = findById(storeKey, id) as Row
      if (key === 'inscriptions' && data.statut !== row.statut) {
        await apiCreate('historiqueStatutInscription', { inscriptionId: id, statutPrecedent: row.statut, statutNouveau: data.statut, dateChangement: new Date().toISOString(), motif: 'Changement de statut via le formulaire' })
      }
      await apiUpdate(storeKey, id, { ...row, ...data })
      showToast(`${entity.label} modifié(e).`)
      if (key === 'reductionsAppliquees') await recomputeEcheancierRemise(row.inscriptionId)
    }
    else {
      const fixed = fixedValues(key)
      const created = await apiCreate(storeKey, { ...data, ...fixed })
      const patch: Record<string, unknown> = {}
      if (key === 'unitesEnseignement' && !created.code) patch.code = generateUeCode()
      const saved = Object.keys(patch).length ? await apiUpdate(storeKey, created.id as string, { ...created, ...patch }) : created
      showToast(`${entity.label} créé(e).`)
      if (key === 'reductionsAppliquees') await recomputeEcheancierRemise(saved.inscriptionId)
      finalId = saved.id as string

      if (key === 'unitesEnseignement' && ecueInlineIds.length) {
        for (const mid of ecueInlineIds) {
          const m = findById('matieres', mid)
          if (m) await apiUpdate('matieres', mid, { ...m, ueId: finalId })
        }
        showToast(`UE créée avec ${ecueInlineIds.length} matière${ecueInlineIds.length > 1 ? 's' : ''} rattachée${ecueInlineIds.length > 1 ? 's' : ''}.`)
      }

      const resume = useUeResumePrefill()
      if (key === 'matieres' && resume.value) {
        const resumePrefill = { ...resume.value, ecueInlineIds: [saved.id] }
        resume.value = null
        closeModal()
        render()
        openForm('unitesEnseignement', null, resumePrefill)
        return
      }
    }
    if (key === 'offres' && niveauIdsForOffre && finalId) await syncNiveauOffre(finalId, niveauIdsForOffre)
  }
  catch (e) {
    console.error('[Registre] submitForm:', e)
    showToast('Opération impossible.')
    return
  }
  closeModal()
  render()
}

/** `generateUeCode` de la maquette. */
function generateUeCode(): string {
  const nums = (useDb().value.unitesEnseignement ?? []).map((u) => {
    const m = /^UE(\d+)$/i.exec(String(u.code ?? ''))
    return m ? Number(m[1]) : 0
  })
  return `UE${(nums.length ? Math.max(...nums) : 0) + 1}`
}

/** Suppression directe (comme `apiDelete` de `useTable.ts`, dupliquée ici pour ne pas dépendre d'un export interne). */
async function apiRemove(collection: string, id: string): Promise<void> {
  await $fetch(`/api/${collection}/${id}`, { method: 'DELETE' })
  const db = useDb()
  db.value = { ...db.value, [collection]: (db.value[collection] ?? []).filter(r => r.id !== id) }
}

/** `syncNiveauOffre` de la maquette : les lignes `niveauOffre` d'une offre suivent exactement les niveaux cochés dans le formulaire. */
async function syncNiveauOffre(offreId: string, niveauIds: string[]): Promise<void> {
  const current = (useDb().value.niveauOffre ?? []).filter(no => no.offreId === offreId)
  for (const no of current) {
    if (!niveauIds.includes(no.niveauId as string)) await apiRemove('niveauOffre', no.id)
  }
  for (const nid of niveauIds) {
    if (!current.some(no => no.niveauId === nid)) {
      const niveau = findById('niveaux', nid)
      await apiCreate('niveauOffre', { offreId, niveauId: nid, libelleLocal: '', volumeHoraire: null, ordreDansOffre: niveau?.ordre ?? 1 })
    }
  }
}
